import { Button, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material'
import Grid from '@mui/material/Grid'
import { ConfirmTextObject } from 'helpers/settingsModal.helper/settingsModal.helper'
import React, { ReactElement, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Dialog } from 'ui/components/Dialog/Dialog'

type ConfirmDialogProps = {
  open: boolean
  onClose: () => void
  onConfirm: () => void
  textObject: ConfirmTextObject
  fullWidth?: boolean
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false
  children?: ReactNode
  subTextList?: string[]
  /**
   * Which button holds focus when the dialog opens. Defaults to the confirm button; a dialog
   * confirming a destructive action passes `deny`, so Enter cannot complete it on open.
   */
  initialFocus?: 'confirm' | 'deny'
  confirmButtonStyle?: React.CSSProperties
  alternativeButton?: {
    text: string
    onClick: () => void
  }
}

export default function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  textObject,
  fullWidth,
  maxWidth,
  children,
  subTextList,
  initialFocus = 'confirm',
  confirmButtonStyle,
  alternativeButton,
}: ConfirmDialogProps): ReactElement {
  const { t } = useTranslation()

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth={fullWidth}
      maxWidth={maxWidth}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      slotProps={{ paper: { 'data-testid': 'confirm-dialog' } }}
    >
      <DialogTitle id="alert-dialog-title">
        <Grid container sx={{ flexGrow: 1 }} direction="row" justifyContent="space-between">
          <Grid>{textObject.title}</Grid>
        </Grid>
      </DialogTitle>
      <DialogContent>
        <div id="alert-dialog-description">
          <DialogContentText>{textObject.text}</DialogContentText>
          {subTextList ? subTextList.map((i, index) => <DialogContentText key={index}>{i}</DialogContentText>) : null}
        </div>
        {children}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          color="secondary"
          variant="contained"
          autoFocus={initialFocus === 'deny'}
          sx={alternativeButton ? { mr: 2 } : undefined}
        >
          {textObject?.deny ?? t(`No`)}
        </Button>
        {alternativeButton && (
          <Button onClick={alternativeButton.onClick} color="secondary" variant="contained">
            {t(alternativeButton.text)}
          </Button>
        )}
        <Button
          style={confirmButtonStyle}
          onClick={() => {
            onConfirm()
          }}
          color="primary"
          variant="contained"
          autoFocus={initialFocus === 'confirm'}
        >
          {textObject?.confirm ?? t(`Yes`)}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
